import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithRedirect, getRedirectResult, signOut, signInWithCustomToken, linkWithPopup } from 'firebase/auth';

const firebaseConfig = {
  apiKey: "AIzaSyCbiS-mTXSoRxB-7DK5QbhLrwXg9kA7ZiE",
  authDomain: "app.katana-ai.studio",
  projectId: "katana-ai-491615",
  storageBucket: "katana-ai-491615.firebasestorage.app",
  messagingSenderId: "990453040288",
  appId: "1:990453040288:web:6620a0de5959d923e635c5",
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();

export const signInWithGoogle = () => signInWithRedirect(auth, googleProvider);
export { getRedirectResult };
export const signOutUser = () => signOut(auth);

// Native Firebase link: attach a Google provider to the currently
// signed-in (Discord-only) user. Used by the settings page when a user
// signed in with Discord wants to also use Google.
export async function linkGoogleToCurrentUser() {
  if (!auth.currentUser) throw new Error('Not signed in');
  return linkWithPopup(auth.currentUser, googleProvider);
}

// ── Discord popup / redirect OAuth ──────────────────────────────────────────
// Desktop: popup window + postMessage handshake.
// Mobile (iOS Safari especially): popups are unreliable / silently blocked,
// so we do a full-page redirect to Discord and let DiscordCallback finish
// the flow inline after we land back at /auth/discord/callback.
function isMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent || '');
}

function startDiscordRedirect(authUrl, mode) {
  try {
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}` || '/';
    sessionStorage.setItem('discordOAuth', JSON.stringify({ mode, returnTo }));
  } catch (_) { /* sessionStorage may be unavailable; fall back to '/' */ }
  window.location.href = authUrl;
  // Navigation in flight; return a never-resolving promise so callers wait.
  return new Promise(() => {});
}

function runDiscordPopup(authUrl) {
  return new Promise((resolve, reject) => {
    const w = 520, h = 720;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top  = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(
      authUrl, 'discord-oauth',
      `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no`
    );
    if (!popup) {
      reject(new Error('Popup blocked. Please allow popups for this site and try again.'));
      return;
    }
    let settled = false;
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      clearInterval(closeTimer);
    };
    const onMessage = (ev) => {
      if (ev.source !== popup) return;
      const data = ev.data;
      if (!data || data.type !== 'discord-auth') return;
      settled = true;
      cleanup();
      if (data.ok) resolve(data);
      else reject(new Error(data.error || 'Discord sign-in failed'));
    };
    window.addEventListener('message', onMessage);
    const closeTimer = setInterval(() => {
      if (popup.closed && !settled) {
        settled = true;
        cleanup();
        reject(new Error('Sign-in window was closed'));
      }
    }, 500);
  });
}

export async function signInWithDiscord() {
  const res = await fetch(`${API_BASE}/api/auth/discord/start-signin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ origin: window.location.origin }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to start Discord sign-in');
  const { authUrl } = await res.json();
  if (isMobileDevice()) return startDiscordRedirect(authUrl, 'signin');
  const result = await runDiscordPopup(authUrl);
  if (!result.customToken) throw new Error('Sign-in did not return a token');
  await signInWithCustomToken(auth, result.customToken);
  return result;
}

export async function linkDiscordToCurrentUser() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/auth/discord/start-link`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ origin: window.location.origin }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to start Discord link');
  const { authUrl } = await res.json();
  if (isMobileDevice()) return startDiscordRedirect(authUrl, 'link');
  return runDiscordPopup(authUrl);
}

export async function unlinkDiscordFromCurrentUser() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/auth/discord/unlink`, {
    method: 'POST', headers, body: '{}',
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to unlink Discord');
  return res.json();
}

export async function unlinkGoogleFromCurrentUser() {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}/api/auth/google/unlink`, {
    method: 'POST', headers, body: '{}',
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to unlink Google');
  // Force a token refresh so the SPA's auth.currentUser.providerData reflects
  // the unlink without requiring a manual reload.
  try { if (auth.currentUser) await auth.currentUser.getIdToken(true); } catch (_) {}
  return res.json();
}

// ── Server-time offset ───────────────────────────────────────────────────────
// The user's local clock can drift relative to Cloud Run's NTP-synced
// clock by tens of seconds. Anywhere we compare a server-stamped
// timestamp (e.g. processingStartedAt) to "now", we'd subtract the
// wrong baseline if Date.now() differs from the server clock.
//
// We learn the offset from any backend response's Date header: it's
// always there (Express sets it), it's accurate to the second, and
// captureServerTime() is essentially free. After the first successful
// fetch the offset is locked in for the session, so callers should
// use serverNow() in place of Date.now() for any duration-vs-server
// math (e.g. ProcessingTimer).
let _serverTimeOffsetMs = 0;
let _serverTimeMeasured = false;
export function captureServerTime(response) {
  try {
    const dateStr = response && response.headers && response.headers.get && response.headers.get('date');
    if (!dateStr) return;
    const serverMs = Date.parse(dateStr);
    if (!isFinite(serverMs)) return;
    _serverTimeOffsetMs = serverMs - Date.now();
    _serverTimeMeasured = true;
  } catch (_) { /* ignore - keep last good offset */ }
}
export function serverNow()        { return Date.now() + _serverTimeOffsetMs; }
export function serverTimeOffset() { return _serverTimeOffsetMs; }
export function isServerTimeMeasured() { return _serverTimeMeasured; }

// Pick the backend based on which frontend host we're loaded from, so one
// committed build works for both environments without manual flipping.
// Prod hosts (app.katana-ai.studio, katana-ai-studio.web.app) - prod backend
// Everything else (dev hosting, localhost) - dev backend
export const API_BASE = (() => {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isProd = host === 'app.katana-ai.studio' || host === 'katana-ai-studio.web.app';
  return isProd
    ? 'https://katana-backend-prod-990453040288.us-south1.run.app'
    : 'https://katana-backend-dev-990453040288.us-south1.run.app';
})();

// One-shot at module load: hit /health and seed the server-time offset
// from the Date response header. Subsequent api.* calls will keep it
// fresh through their own captureServerTime() invocations. /health is
// public, cheap, and in the same Cloud Run service as everything else.
if (typeof window !== 'undefined') {
  fetch(`${API_BASE}/health`).then(captureServerTime).catch(() => {});
}

async function getAuthHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
}

// True for an active job that's already been handed to the provider. The
// backend refuses to cancel those (DELETE /api/jobs/:id answers 409): the
// studio is billed either way, so the job finishes and the user is charged.
// Mirrors that check so the UI can lock the delete button.
export function isJobSentToProvider(job) {
  if (!job || (job.status !== 'queued' && job.status !== 'processing')) return false;
  return job.status === 'processing' || !!job.submitClaimedAt || !!job.autoResubmittedAt;
}
export const JOB_SENT_TO_PROVIDER_HINT = "Already sent to the provider, so it can't be cancelled. You'll be charged when it finishes, and can delete it after.";

export const api = {
  async uploadImage(file) {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const token = await user.getIdToken();
    const formData = new FormData();
    formData.append('image', file);
    const res = await fetch(`${API_BASE}/api/jobs/upload-image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Upload failed');
    return res.json();
  },

  // Forwards the entire payload to the backend rather than cherry-picking
  // fields. Earlier this destructured a fixed list and silently dropped new
  // fields (outputType, inputMode, referenceImageGcsPaths, n, etc.), which
  // is why image-gen / ref-to-video / text-to-video submissions all came
  // out the other side as plain text2video. Pass through whatever the
  // caller sends - the backend validates.
  async submitJob(payload) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/jobs`, {
      method: 'POST', headers,
      body: JSON.stringify(payload || {}),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Submit failed');
    return res.json();
  },

  // Proxy URLs for authenticated file access. Pass `{ thumb: true }` to
  // request the 320px thumbnail variant; the backend falls back to full-res
  // if a thumb isn't available yet (e.g. brand-new content or pre-backfill).
  getImageUrl(jobId, opts) { return `${API_BASE}/api/files/images/${jobId}${opts && opts.thumb ? '?thumb=1' : ''}`; },
  getEndImageUrl(jobId, opts) { return `${API_BASE}/api/files/end-images/${jobId}${opts && opts.thumb ? '?thumb=1' : ''}`; },
  getVideoUrl(jobId) { return `${API_BASE}/api/files/videos/${jobId}`; },
  getImageByIdUrl(imageId, opts) { return `${API_BASE}/api/jobs/image/${imageId}${opts && opts.thumb ? '?thumb=1' : ''}`; },

  async deleteImage(imageId) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/jobs/image/${imageId}`, { method: 'DELETE', headers });
    if (!res.ok) throw new Error((await res.json()).error || 'Delete image failed');
    return res.json();
  },

  async listImages() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/jobs/images`, { headers });
    if (!res.ok) throw new Error((await res.json()).error || 'List images failed');
    return res.json();
  },

  async listJobs(limit, status, outputType) {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    if (limit !== undefined) params.set('limit', String(limit));
    if (status) params.set('status', status);
    if (outputType) params.set('outputType', outputType);
    const qs = params.toString();
    const res = await fetch(`${API_BASE}/api/jobs${qs ? `?${qs}` : ''}`, { headers });
    if (!res.ok) throw new Error((await res.json()).error || 'List failed');
    return res.json();
  },
  // Image-output streaming URL (for the image-mode generate panel + library)
  getOutputImageUrl(jobId, index, opts) {
    return `${API_BASE}/api/files/output-images/${jobId}/${index}${opts && opts.thumb ? '?thumb=1' : ''}`;
  },
  // Reference image streaming URL (per-index)
  getReferenceImageUrl(jobId, index, opts) {
    return `${API_BASE}/api/files/reference-images/${jobId}/${index}${opts && opts.thumb ? '?thumb=1' : ''}`;
  },
  // Poster (first-frame JPEG) for a video job, used as a thumbnail when
  // there's no source frame to display (ref2video / text2video).
  getPosterUrl(jobId, opts) {
    return `${API_BASE}/api/files/posters/${jobId}${opts && opts.thumb ? '?thumb=1' : ''}`;
  },

  async getJob(jobId) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/jobs/${jobId}`, { headers });
    if (!res.ok) throw new Error((await res.json()).error || 'Get failed');
    return res.json();
  },

  async deleteJob(jobId) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/jobs/${jobId}`, { method: 'DELETE', headers });
    if (!res.ok) throw new Error((await res.json()).error || 'Delete failed');
    return res.json();
  },

  async getUsage() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/usage?months=3`, { headers });
    if (!res.ok) throw new Error((await res.json()).error || 'Usage failed');
    return res.json();
  },

  async getSettings() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/settings`, { headers });
    if (!res.ok) throw new Error((await res.json()).error || 'Settings failed');
    return res.json();
  },

  async loadGenerateSettings() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/settings/generate`, { headers });
    if (!res.ok) throw new Error((await res.json()).error || 'Load settings failed');
    return res.json();
  },

  async saveGenerateSettings(settings) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/settings/generate`, {
      method: 'POST', headers, body: JSON.stringify(settings),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Save settings failed');
    return res.json();
  },

  // ── Generate Presets ─────────────────────────────────────────────────────
  async getGeneratePresets() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/settings/generate/presets`, { headers });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to load presets');
    return res.json();
  },
  async saveGeneratePreset(preset) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/settings/generate/presets`, {
      method: 'POST', headers, body: JSON.stringify(preset),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to save preset');
    return res.json();
  },
  async renameGeneratePreset(id, name) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/settings/generate/presets/${id}`, {
      method: 'PUT', headers, body: JSON.stringify({ name }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to rename preset');
    return res.json();
  },
  async deleteGeneratePreset(id) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/settings/generate/presets/${id}`, {
      method: 'DELETE', headers,
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to delete preset');
    return res.json();
  },

  async createPaymentIntent(pkg) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/stripe/create-payment-intent`, {
      method: 'POST', headers, body: JSON.stringify({ package: pkg }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Payment setup failed');
    return res.json();
  },

  async confirmPayment(paymentIntentId) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/stripe/confirm-payment`, {
      method: 'POST', headers, body: JSON.stringify({ paymentIntentId }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Payment confirmation failed');
    return res.json();
  },

  async claimWelcomeCredits() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/settings/claim-welcome-credits`, {
      method: 'POST', headers, body: '{}',
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to claim welcome credits');
    return res.json();
  },

  async saveAccountSettings(settings) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/settings/account`, {
      method: 'POST', headers, body: JSON.stringify(settings),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Save failed');
    return res.json();
  },

  // ── Admin ──────────────────────────────────────────────────────────────────
  async getAdminUsers() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/users`, { headers });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to load users');
    return res.json();
  },
  async addAdminUser(email, role) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/users`, {
      method: 'POST', headers, body: JSON.stringify({ email, role }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to add user');
    return res.json();
  },
  async updateAdminUser(email, updates) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(email)}`, {
      method: 'PUT', headers, body: JSON.stringify(updates),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to update user');
    return res.json();
  },
  async giftCredits(uid, amount, email) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/gift-credits/${uid || '_byemail'}`, {
      method: 'POST', headers, body: JSON.stringify({ amount, email }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to gift credits');
    return res.json();
  },
  async deleteAdminUser(email) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(email)}`, {
      method: 'DELETE', headers,
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to remove user');
    return res.json();
  },

  // ── Thumbnail backfill ────────────────────────────────────────────────────
  // Pass `{ force: true }` to ignore the per-file existence check and
  // regenerate every thumbnail. Use after a thumb-path scheme change.
  async startThumbnailBackfill(opts) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/thumbnails/backfill`, {
      method: 'POST', headers, body: JSON.stringify({ force: !!(opts && opts.force) }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to start backfill');
    return res.json();
  },
  async getThumbnailBackfillStatus() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/thumbnails/backfill/status`, { headers });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to fetch status');
    return res.json();
  },

  // ── Studio Keys (Admin) ────────────────────────────────────────────────────
  async getStudioKeys() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/studio-keys`, { headers });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to load studio keys');
    return res.json();
  },
  async saveStudioKeys(keys) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/studio-keys`, {
      method: 'POST', headers, body: JSON.stringify(keys),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to save studio keys');
    return res.json();
  },
  async deleteStudioKey(which) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/studio-keys?key=${which}`, {
      method: 'DELETE', headers,
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to delete studio key');
    return res.json();
  },
  async copyMyKeysToStudio() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/studio-keys/copy-from-me`, {
      method: 'POST', headers, body: '{}',
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to copy keys');
    return res.json();
  },

  // ── Finances (Admin) ──────────────────────────────────────────────────────
  async getFinances() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/finances`, { headers });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to load finances');
    return res.json();
  },
  async checkStudioGrokBalance() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/finances/grok-check`, { method: 'POST', headers, body: '{}' });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to check Grok balance');
    return res.json();
  },
  async checkStudioGoogleSpend(days = 30) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/finances/google-check`, {
      method: 'POST', headers, body: JSON.stringify({ days }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to check Google spend');
    return res.json();
  },
  async checkStudioRecraftBalance() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/finances/recraft-check`, { method: 'POST', headers, body: '{}' });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to check Recraft balance');
    return res.json();
  },
  async checkStudioBflBalance() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/finances/bfl-check`, { method: 'POST', headers, body: '{}' });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to check BFL balance');
    return res.json();
  },
  async checkStudioOpenAISpend(days = 30) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/finances/openai-spend`, {
      method: 'POST', headers, body: JSON.stringify({ days }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to check OpenAI spend');
    return res.json();
  },
  async checkStudioGoogleBilling() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/finances/google-status`, { method: 'POST', headers, body: '{}' });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to check Gemini billing');
    return res.json();
  },
  async saveFinances(data) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/finances`, {
      method: 'POST', headers, body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to save finances');
    return res.json();
  },

  // ── App Config (Admin) ─────────────────────────────────────────────────────
  async getAppConfig() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/app-config`, { headers });
    if (!res.ok) throw new Error('Failed to load app config');
    return res.json();
  },
  async saveAppConfig(data) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/app-config`, {
      method: 'POST', headers, body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to save app config');
    return res.json();
  },

  // ── Job Queue (Admin) ──────────────────────────────────────────────────────
  async getJobQueue() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/job-queue`, { headers });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to load job queue');
    return res.json();
  },
  // Full admin view of a single job - returns the entire Firestore doc
  // with timestamps serialized. Used by the admin job-detail page.
  async getAdminJob(jobId) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/jobs/${jobId}`, { headers });
    if (!res.ok) throw new Error((await res.json().catch(()=>({}))).error || 'Failed to load job');
    return res.json();
  },
  // Recent Grok jobs with estimated vs actual cost. Used to diagnose
  // pre-submit price drift.
  async getGrokCostAudit(limit = 50) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/grok-cost-audit?limit=${limit}`, { headers });
    if (!res.ok) throw new Error((await res.json().catch(()=>({}))).error || 'Failed to load Grok cost audit');
    return res.json();
  },
  // Look in GCS for outputs of a job whose Firestore doc didn't get
  // imageOutputGcsPaths written (e.g. instance died after the upstream
  // API call but before the doc update). Restores the job to ready if
  // any matching files exist.
  async recoverJobOutputs(jobId) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/jobs/${jobId}/recover-outputs`, {
      method: 'POST', headers, body: '{}',
    });
    if (!res.ok) throw new Error((await res.json().catch(()=>({}))).error || 'Recovery failed');
    return res.json();
  },
  // Per-job recovery action. action: 'resume' (re-fire pollJob),
  // 'mark-failed' (set status=failed so it stops occupying a slot), or
  // 'check-now' (synchronous one-shot upstream poll inside the request).
  async recoverJob(jobId, action = 'resume') {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/admin/jobs/${jobId}/recover`, {
      method: 'POST', headers, body: JSON.stringify({ action }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Recovery failed');
    return res.json();
  },

  // ── Access Requests ─────────────────────────────────────────────────────────
  async createAccessRequest() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/access-requests`, {
      method: 'POST', headers, body: JSON.stringify({}),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to create request');
    return res.json();
  },
  async updateAccessRequest(id, { deviantart, message }) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/access-requests/${id}`, {
      method: 'PATCH', headers, body: JSON.stringify({ deviantart, message }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to update request');
    return res.json();
  },
  async getAccessRequests() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/access-requests`, { headers });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to load requests');
    return res.json();
  },
  async getUnreadRequestCount() {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/access-requests/unread-count`, { headers });
    if (!res.ok) return { count: 0 };
    return res.json();
  },
  async updateAccessRequestAdmin(id, { action, role, rejectMessage }) {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/api/access-requests/${id}`, {
      method: 'PUT', headers, body: JSON.stringify({ action, role, rejectMessage }),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Failed to update request');
    return res.json();
  },
};
